import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/api/client";

const SiteSettingsContext = createContext(null);

const DEFAULTS = {
  site_name: "FarmersAgroHub",
  tagline: "Connecting Farmers, Empowering Agriculture",
  short_tagline: "Trusted Agricultural Marketplace",
  logo_url: "https://customer-assets.emergentagent.com/job_build-web-905/artifacts/cd4lv2zk_52a8ffae-0841-40cd-9c81-57806e3e0ff5.png",
  primary_color: "#1B5E20",
  accent_color: "#F57F17",
  hero_eyebrow: "India's #1 Multi-Vendor Agri Marketplace",
  hero_title_line1: "Connecting Farmers,",
  hero_title_highlight: "Empowering",
  hero_title_line2: "Agriculture",
  hero_subtitle: "Genuine seeds, pesticides, fertilizers, irrigation & farm machinery from verified GST-registered sellers. Delivered to 18,000+ pincodes across India.",
  hero_image: "https://images.pexels.com/photos/20515274/pexels-photo-20515274.jpeg",
  hero_cta_text: "Find Products",
  contact_email: "contact@farmersagrohub.com",
  contact_phone: "+91 80902 45875",
  whatsapp_number: "918090245875",
  contact_address: "FarmersAgroHub HQ, Mirzapur, Uttar Pradesh, Pincode 231310, India",
  featured_product_ids: [],
};

// Convert #RRGGBB to "H S% L%" (HSL components for Tailwind CSS vars)
function hexToHsl(hex) {
  if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/meta/site-settings");
      setSettings({ ...DEFAULTS, ...data });
    } catch {}
    setLoaded(true);
  };

  useEffect(() => { refresh(); }, []);

  // Apply theme colors as CSS variables
  useEffect(() => {
    const primaryHsl = hexToHsl(settings.primary_color);
    const accentHsl = hexToHsl(settings.accent_color);
    if (primaryHsl) {
      document.documentElement.style.setProperty("--primary", primaryHsl);
      document.documentElement.style.setProperty("--ring", primaryHsl);
    }
    if (accentHsl) {
      document.documentElement.style.setProperty("--secondary", accentHsl);
    }
    // Title is now managed by per-page <SeoHead> (react-helmet-async). Don't override here.
  }, [settings]);

  return (
    <SiteSettingsContext.Provider value={{ settings, setSettings, refresh, loaded }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
